/// <reference types="codemirror" />
/// <reference types="codemirror/addon/hint/show-hint.d.ts" />

import { notNullish, notEmpty, parseBoolean, parseString, parseDefaultBoolean } from "@de-xima/xima-common-js-lang";
import { ResizeSensor } from "css-element-queries";
import $ from "jquery";

import { bindThis, parseNumber, parseThenable } from "./Lang.ts";
import { showNotification } from "./Notification.ts";
import {
  findWidgetForElement,
  getCodeMirror,
  getWidgetByVar,
  overridePrivatePrototypeMethod,
  overridePrototypeMethod,
} from "./Util.ts";
import { byCssAs, byCssHtml, byId } from "@de-xima/xima-common-js-dom";

function replaceFileUploadParams(
  text: string,
  message: PrimeFaces.widget.FileUpload.UploadMessage,
  fileCount: number,
  widget: PrimeFaces.widget.FileUpload | PrimeFaces.widget.SimpleFileUpload,
): string {
  return text
    .replace("{accept}", widget.jq.find("input[accept]").attr("accept") ?? "")
    .replace("{fileLimit}", String(widget.cfg.fileLimit ?? 0))
    .replace("{fileCount}", String(fileCount))
    .replace("{maxSize}", widget.formatSize(widget.cfg.maxFileSize ?? 0))
    .replace("{name}", message.filename)
    .replace("{size}", widget.formatSize(message.filesize));
}

function replaceFileUploadParamsForFileUpload(
  text: string,
  message: PrimeFaces.widget.FileUpload.UploadMessage,
  widget: PrimeFaces.widget.FileUpload,
): string {
  return replaceFileUploadParams(text, message, widget.uploadedFileCount + widget.files.length + 1, widget);
}

function replaceFileUploadParamsForSimpleFileUpload(
  text: string,
  message: PrimeFaces.widget.FileUpload.UploadMessage,
  widget: PrimeFaces.widget.SimpleFileUpload,
): string {
  const input = widget.input[0];
  const fileCount = input instanceof HTMLInputElement ? (input.files?.length ?? 0) : 0;
  return replaceFileUploadParams(text, message, fileCount, widget);
}

/**
 * Refreshes the layout of the code mirror editor when initially loaded.
 * Otherwise code mirror stays at a height of 0.
 * @param layout JQuery layout instance.
 */
export function resizeEditorOnLoaded(layout: JQueryLayout.LayoutInstance): void {
  layout.container.find(".CodeMirror").each((_key, element) => {
    const cm = getCodeMirror(element);
    cm?.refresh();
  });
}

/**
 * When a follow up label is autofilled, Firefox only triggers "input" and "change".
 *
 * To make follow up labels work correctly, we need to trigger "blur". This checks
 * whether a value is set on the input field, and if so, adds the class "ui-state-filled".
 */
export function followUpLabelAutocomplete(): void {
  jQuery(document).on("input", ".ui-inputfield", function (this: HTMLElement, _event: JQuery.TriggeredEvent) {
    if (document.activeElement !== this) {
      jQuery(this).trigger("blur");
    }
  });
}

// TODO Check if necessary, fixed for some menus in PF
/**
 * The `<p:menu />` is used to create a button with a menu that appears when that button is clicked. The menu
 * is positioned so that its top or bottom edge is aligned with the menu button. When the menu contains many menu
 * entries, the menu may not fit the screen. JQueryUI provides an option to move the menu back inside the browser
 * window, but that options is not exposed from the PrimeFaces component. This overrides a method of the `menu`
 * to add proper positioning.
 *
 * Use this fix as follows:
 *
 ```xml
 <p:menu a:data-collision="flipfit">
 </p:menuButton>
 ```
 */
export function fixMenuPositioning(): void {
  if (
    typeof PrimeFaces === "object" &&
    typeof PrimeFaces.widget === "object" &&
    typeof PrimeFaces.widget.Menu === "function"
  ) {
    overridePrivatePrototypeMethod(
      PrimeFaces.widget.TieredMenu,
      "initOverlay",
      // biome-ignore lint/complexity/useLiteralKeys: We need to access a private member
      PrimeFaces.widget.Menu.prototype["initOverlay"],
      function (this: PrimeFaces.widget.Menu, superMethod) {
        superMethod();
        const collision = parseString(this.jq.data("collision"));
        if (notNullish(this.cfg.pos)) {
          this.cfg.pos.collision = collision ?? "flip";
        }
      },
    );
  }
}

/**
 * Overrides a method of the FileUpload widget from PrimeFaces to provide custom functionality.
 *
 * For a `<p:fileUpload styleClass="fc-fileupload-growl" />` that has the style class `fc-fileupload-growl` set, shows
 * validation error messages (file size exceed, invalid extension) as a global growl message.
 *
 * For each `<p:fileUpload />`, also replaces a few more variables to allow for better error messages:
 * - `{accept}`: The file extensions that are accepted.
 * - `{fileLimit}`: The current file limit (allowed number of uploaded files)
 * - `{fileCount}`: Current number of uploaded files
 * - `{maxSize}`: The current file limit (formatted appropriately as Byte, MB, GB etc.)
 * @this {unknown}
 */
export function showFileUploadMessagesAsGrowl(): void {
  if (
    typeof PrimeFaces === "object" &&
    typeof PrimeFaces.widget === "object" &&
    typeof PrimeFaces.widget.FileUpload === "function"
  ) {
    overridePrivatePrototypeMethod(
      PrimeFaces.widget.FileUpload,
      "showMessage",
      // biome-ignore lint/complexity/useLiteralKeys: We need to access a private member
      PrimeFaces.widget.FileUpload.prototype["showMessage"],
      function (this: PrimeFaces.widget.FileUpload, superMethod, message) {
        const showAsGrowl = this.jq.hasClass("fc-fileupload-growl");
        const summary = replaceFileUploadParamsForFileUpload(message.summary, message, this);
        if (showAsGrowl) {
          const detail =
            notEmpty(message.filename) && message.filesize !== undefined
              ? replaceFileUploadParamsForFileUpload(this.cfg.messageTemplate ?? "", message, this)
              : "";
          showNotification(
            {
              detail: detail,
              severity: "error",
              summary: summary,
            },
            undefined,
            false,
          );
        } else {
          superMethod({
            filename: message.filename,
            filesize: message.filesize,
            summary: summary,
          });
        }
      },
    );
  }
}

/**
 * Creates a function that can be used for a PrimeFaces simple file upload widget. Takes
 * the validation failure message and shows it in a growl. Use it like this:
 *
 * ```xml
 * <p:fileUpload
 *   mode="simple"
 *   onvalidationfailure="showGrowlOnSimpleFileUploadValidatorFailure('widgetVar', msg)"
 * />
 * ```
 * @param widgetVar The widget variable of the SimpleFileUpload widget.
 * @param message The message to show in the growl (as the detail).
 * @param summary The localized summary of the growl message. Defaults to `error`.
 */
export function showGrowlOnSimpleFileUploadValidatorFailure(
  widgetVar: string,
  message: PrimeFaces.widget.FileUpload.UploadMessage,
  summary?: string,
): void {
  const widget = getWidgetByVar(widgetVar, PrimeFaces.widget.SimpleFileUpload);
  if (widget === undefined) {
    return;
  }
  const renderedMessage = replaceFileUploadParamsForSimpleFileUpload(message.summary, message, widget);
  showNotification(
    {
      detail: renderedMessage,
      severity: "error",
      summary: summary ?? "error",
    },
    undefined,
    false,
  );
}

/**
 * Due to some bug, there are no columns when first loading the PrimeFaces
 * extensions sheet. This causes an error during the JavaScript initialization,
 * which stops other JavaScript from being executed. This overwrites the
 * `PrimeFaces.widget.ExtSheet.prototype._render` to catch any possible errors
 * (and log them).
 */
export function catchErrorsExtSheet(): void {
  if (
    typeof PrimeFaces === "object" &&
    typeof PrimeFaces.widget === "object" &&
    typeof PrimeFaces.widget.ExtSheet === "function"
  ) {
    overridePrivatePrototypeMethod(
      PrimeFaces.widget.ExtSheet,
      "_render",
      // biome-ignore lint/complexity/useLiteralKeys: We need to access a private member
      PrimeFaces.widget.ExtSheet.prototype["_render"],
      function (this: PrimeFaces.widget.ExtSheet, oldMethod) {
        try {
          oldMethod();
        } catch (e) {
          console.error("Could not _render PrimeFaces.widget.ExtSheet:", e);
        }
      },
    );
  }
}

/**
 * Adds a resize observer (ResizeSensor) to all nano content children, and refreshes the nano scroller
 * when any size changes occur.
 * @deprecated Use simplebar, like in the designer.
 */
export function fixNanoScroller(): void {
  for (const nano of jQuery(".nano")) {
    const $nano = jQuery(nano);
    const refreshNano = () => {
      try {
        $nano.nanoScroller();
      } catch {
        // ignore, happens occasionally
      }
    };

    const initial = !parseBoolean($nano.data("nanoInitRefresh"));
    let needsRefresh = initial;
    $nano.data("nanoInitRefresh", true);

    // Listen to sizes in the scroll container window
    const nanoContent = jQuery(nano).children(".nano-content");
    // If already attached, do not attach again.
    const nanoContentEl = nanoContent.get(0);
    if (nanoContent.data("resizeSensor") === undefined && nanoContentEl !== undefined) {
      nanoContent.data("resizeSensor", new ResizeSensor(nanoContentEl, refreshNano));
    }

    // Listen to size changes of the scrollable content
    for (const item of nanoContent.children()) {
      const $item = jQuery(item);
      // If already attached, do not attach again. Also do not attach resize sensor to a resize sensor helper.
      if ($item.data("resizeSensor") !== undefined || $item.hasClass("resize-sensor")) {
        continue;
      }
      $item.data("resizeSensor", new ResizeSensor(item, refreshNano));
      // Needs to refresh when a child was added by an AJAX request.
      needsRefresh = needsRefresh || !initial;
    }

    // Do a refresh the very first time; or when the ResizeSensor helper was removed by an AJAX update and
    // had to be reattached.
    if (needsRefresh) {
      refreshNano();
    }
  }
}

/**
 * @deprecated Investigate whether https://github.com/KingSora/OverlayScrollbars is better
 * Removes the nano scroller from the given elements, including changes made by
 * `fixNanoScroller`.
 * @param nanos (List of) nano scroller elements.
 */
export function removeNanoScroller(nanos: JQuery): void {
  for (const nano of nanos) {
    const $nano = jQuery(nano);
    const nanoContent = $nano.children().first();

    $nano.nanoScroller({ destroy: true });
    $nano.removeClass("nano");
    nanoContent.removeClass("nano-content");
    nanoContent.removeAttr("style");

    const nanoContentEl = nanoContent.get(0);
    if (nanoContentEl !== undefined) {
      ResizeSensor.detach(nanoContentEl);
      ResizeSensor.detach(nanoContent.children().toArray());
    }
    $nano.removeData("nanoInitRefresh");
    nanoContent.removeData("resizeSensor");
    nanoContent.children().removeData("resizeSensor");
  }
}

export function fixPasswordForCustomStrengthCheck(): void {
  const strengthMap = new WeakMap<HTMLElement, { strength: number; password: string }>();
  overridePrototypeMethod<PrimeFaces.widget.Password, "testStrength">(
    PrimeFaces.widget.Password,
    "testStrength",
    function (this: PrimeFaces.widget.Password, superMethod, password) {
      const jq = this.jq;
      const el = jq.get(0);
      if (el === undefined) {
        return 0;
      }
      if (strengthMap.has(el) && strengthMap.get(el)?.password === password) {
        return strengthMap.get(el)?.strength ?? 0;
      }
      const methodExpr = parseString(this.jq.data("strengthCheck"));
      const method = notEmpty(methodExpr) ? PrimeFaces.csp.evalResult(methodExpr) : undefined;
      if (typeof method === "function") {
        const strength = method(password) as unknown;
        const thenable = parseThenable(strength);
        if (thenable !== undefined) {
          thenable.then((newStrength) => {
            const newStrengthNumber = parseNumber(newStrength, 0);
            const oldStrength = strengthMap.get(el)?.strength ?? 0;
            if (oldStrength !== newStrength) {
              strengthMap.set(el, { password, strength: newStrengthNumber });
              jq.trigger("keyup");
            }
          });
          return strengthMap.get(el)?.strength ?? 0;
        }

        const newStrength = typeof strength === "number" ? strength : Number.parseFloat(String(strength));
        strengthMap.set(el, { password, strength: newStrength });
        return newStrength;
      }

      return superMethod(password);
    },
  );
}

/**
 * Applies several accessibility related fixes to `<p:splitButton/>`. This
 * method should be called once after page load.
 */
export function fixWcagSplitButton(): void {
  overridePrototypeMethod<PrimeFaces.widget.SplitButton, "init">(
    PrimeFaces.widget.SplitButton,
    "init",
    function (this: PrimeFaces.widget.SplitButton, $super, cfg) {
      $super(cfg);
      this.menuButton.attr("aria-label", this.jq.data("toggleButtonLabel") ?? "Show options");
      this.jq.attr("role", "button");
    },
  );
}

/**
 * Applies several accessibility related fixes to `<p:selectOneMenu/>`. This
 * method should be called once after page load.
 */
export function fixWcagSelectOneMenu(): void {
  overridePrototypeMethod<PrimeFaces.widget.SelectOneMenu, "show">(
    PrimeFaces.widget.SelectOneMenu,
    "show",
    function (this: PrimeFaces.widget.SelectOneMenu, $super) {
      $super();
      this.panel.find(".ui-selectonemenu-list").attr("aria-label", this.cfg.label ?? "");
    },
  );
}

/**
 * Applies several accessibility related fixes to `<p:selectCheckboxMenu/>`. This
 * method should be called once after page load.
 */
export function fixWcagSelectCheckboxMenu(): void {
  overridePrototypeMethod<PrimeFaces.widget.SelectCheckboxMenu, "show">(
    PrimeFaces.widget.SelectCheckboxMenu,
    "show",
    function (this: PrimeFaces.widget.SelectCheckboxMenu, $super) {
      $super();
      const label = $(`[for="${$.escapeSelector(parseString(this.id))}"]`).text();
      this.panel.find(".ui-selectcheckboxmenu-list").attr("aria-label", label);
      this.panel.find(".ui-selectcheckboxmenu-list").attr("role", "listbox");
      this.panel.find(".ui-selectcheckboxmenu-item").attr("role", "option");
      this.panel.find(".ui-selectcheckboxmenu-group-list-item").attr("role", "presentation");
    },
  );
}

/**
 * Applies several accessibility related fixes to `<p:menuButton/>`. This
 * method should be called once after page load.
 */
export function fixWcagMenuButton(): void {
  overridePrototypeMethod<PrimeFaces.widget.MenuButton, "show">(
    PrimeFaces.widget.MenuButton,
    "show",
    function (this: PrimeFaces.widget.MenuButton, $super) {
      $super();
      this.menu.find(".ui-menu-list").attr("role", "presentation");
      this.menuitems.attr("role", "presentation");
    },
  );
}

/**
 * Applies several accessibility related fixes to `<p:menu/>`. This
 * method should be called once after page load.
 */
export function fixWcagMenu(): void {
  overridePrototypeMethod<PrimeFaces.widget.Menu, "show">(
    PrimeFaces.widget.Menu,
    "show",
    function (this: PrimeFaces.widget.Menu, $super) {
      $super();
      this.jq.find(".ui-menu-list").attr("role", "presentation");
      this.jq.find(".ui-menuitem").attr("role", "presentation");
    },
  );
}

/**
 * Adds a "cache" option to the `<p:selectOneMenu />` component. When false, the
 * items are not cached and reloaded every time the select options panel is opened.
 * To use, you must also set the `dynamic` attribute to `true`:
 *
 * ```xml
 * <ui:composition xmlns:pt="http://xmlns.jcp.org/jsf/passthrough">
 *   <p:selectOneMenu dynamic="true" pt:data-cache="false">
 * </ui:composition>
 * ```
 */
export function addNoCacheFeatureToSelectOneMenu(): void {
  overridePrototypeMethod<PrimeFaces.widget.SelectOneMenu, "hide">(
    PrimeFaces.widget.SelectOneMenu,
    "hide",
    function (superMethod) {
      superMethod();
      const cache = parseDefaultBoolean(this.jq.data("cache"), true);
      if (!cache) {
        this.isDynamicLoaded = false;
      }
    },
  );
  overridePrototypeMethod<PrimeFaces.widget.SelectOneMenu, "show">(
    PrimeFaces.widget.SelectOneMenu,
    "show",
    function (superMethod) {
      const cache = parseDefaultBoolean(this.jq.data("cache"), true);
      if (!cache) {
        this.isDynamicLoaded = false;
      }
      superMethod();
    },
  );
}

/**
 * Adds additional conditions for hiding the overlay panel. To use, add the relevant data
 * attributes to the `<p:overlayPanel />` component:
 *
 * ```xml
 * <ui:composition xmlns:pt="http://xmlns.jcp.org/jsf/passthrough">
 *   <p:overlayPanel
 *     pt:data-hide-on-scroll="false"
 *     pt:data-conditional-hide-scroll="true">
 *   </p:overlayPanel>
 * </ui:composition>
 */
export function addConditionalHideFeatureToOverlayPanel(): void {
  overridePrivatePrototypeMethod(
    PrimeFaces.widget.OverlayPanel,
    "handleViewportChange",
    // biome-ignore lint/complexity/useLiteralKeys: We need to access an internal method
    PrimeFaces.widget.OverlayPanel.prototype["handleViewportChange"],
    function (superMethod) {
      const hideOnScroll = parseDefaultBoolean(this.jq.attr("data-hide-on-scroll"), true);
      const currentEvent = window.event;
      let shouldHide = true;
      if (currentEvent !== undefined) {
        if (!hideOnScroll && currentEvent.type === "scroll") {
          shouldHide = false;
        }
      }
      if (!shouldHide) {
        // biome-ignore lint/complexity/useLiteralKeys: Need to access private method
        this["align"](this.target);
        return;
      } else {
        superMethod();
      }
    },
  );
  overridePrototypeMethod<PrimeFaces.widget.SelectOneMenu, "show">(
    PrimeFaces.widget.SelectOneMenu,
    "show",
    function (superMethod) {
      const cache = parseDefaultBoolean(this.jq.data("cache"), true);
      if (!cache) {
        this.isDynamicLoaded = false;
      }
      superMethod();
    },
  );
}

/**
 * Destroys the PrimeFaces.widget.OverlayPanel with the given ID, when it exists.
 *
 * To use this fix, add a <script> tag just before the <p:overlayPanel/> component
 * where you call this function and pass in the fully qualified ID of the overlay panel.
 *
 * https://github.com/primefaces/primefaces/issues/5710
 *
 * May be fixed in PrimeFaces 14.0 with a new attribute destroyWithTarget="true"
 * on the overlayPanel.
 *
 * Workaround for a PrimeFaces bug where the old panel still exists in the DOM
 * when its contents are updated via AJAX. Occurs when the option
 * "appendTo=@(body)" is set on the overlay panel. Results in child components
 * finding duplicate elements, which may cause strange or buggy behavior in
 * certain components.
 *
 * For example, the autocomplete component contains a check for
 * `this.hinput.length === 1`, where `hinput` is a hidden input field.
 * Since the length is now `2` due to the duplicate, it is treated as if
 * it were `0`, which breaks the component (e.g. it won't submit values
 * properly anymore).
 *
 * @param id The fully qualified ID of the overlay panel to destroy.
 */
export function fixPfIssue5710DestroyOverlayPanel(id: string): void {
  const element = document.getElementById(id);
  const widget = findWidgetForElement(element, PrimeFaces.widget.OverlayPanel);
  widget?.destroy();
}

/**
 * Overrides some PrimeFaces widget methods to fix issues with the widgets.
 */
export function fixPrimeFacesWidgets(): void {
  // Some widgets are loaded dynamically after page load
  // We hook into the createWidget method called internally by PrimeFaces
  // whenever a new widget is created. This way, we can ensure we can
  // apply the fix before any widget is created.
  const previous = PrimeFaces.createWidget;
  PrimeFaces.createWidget = function (widgetName, widgetVar, cfg) {
    const proceedCreateWidget = () => {
      try {
        applyAllPrimeFacesWidgetsFixes();
      } catch (e) {
        console.error("Error in fixWidgets", e);
      }
      previous.call(this, widgetName, widgetVar, cfg);
    };
    if (widgetName in PrimeFaces.widget) {
      proceedCreateWidget();
    } else {
      // Widget could be loaded dynamically, delay creation until it is loaded
      const maxWait = 60000; // wait at most 60 seconds
      const pollInterval = 250; // check every 250ms
      const start = Date.now();
      const id = setInterval(() => {
        if (widgetName in PrimeFaces.widget) {
          clearInterval(id);
          proceedCreateWidget();
          // This code normally runs synchronously after the POSTBACK request.
          // However, since we delayed the widget creation, we need to run it again manually.
          PrimeFaces.ajax.Response.destroyDetachedWidgets();
          return;
        }
        if (Date.now() - start > maxWait) {
          clearInterval(id);
          console.warn(`PrimeFaces widget class ${widgetName} not found after ${maxWait}ms, giving up.`);
          return;
        }
      }, pollInterval);
    }
  };
}

function applyAllPrimeFacesWidgetsFixes() {
  fixPrimeFacesTextEditor();
}

/**
 * Fixes the PrimeFaces TextEditor widget. Quill editor provides several options, which
 * we cannot use directly in PrimeFaces. This fix allows us to use these option.
 *
 * __Bounds__
 *
 * See https://quilljs.com/docs/configuration#bounds
 *
 * Just add a data attribute to the component like this:
 * ```xml
 * <p:textEditor pt:data-bounds="css-selector" />
 * ```
 *
 * __Placeholders__
 *
 * Just include the component in a formcycle row with form placeholders enabled:
 * ```xml
 * <xi:row formPlaceholders="true">
 *   <p:textEditor />
 * </xi:row>
 * ```
 *
 * Then add the toolbar button to the component:
 * ```xml
 * <p:textEditor toolbarVisible="true">
 *   <f:facet name="toolbar">
 *     <span class="ql-formats">
 *       <button class="ql-fc-placeholder"></button>
 *     </span>
 *   </f:facet>
 * </p:textEditor>
 * ```
 */
function fixPrimeFacesTextEditor() {
  if (PrimeFaces.widget.TextEditor !== undefined) {
    overridePrivatePrototypeMethod(
      PrimeFaces.widget.TextEditor,
      "_render",
      // biome-ignore lint/complexity/useLiteralKeys: need to access private method
      PrimeFaces.widget.TextEditor.prototype["_render"],
      onPrimeFacesTextEditorRender,
    );
  }
}

function onPrimeFacesTextEditorRender(
  this: PrimeFaces.widget.TextEditor,
  superMethod: PrimeFaces.widget.TextEditor["_render"],
) {
  const bounds = this.jq.attr("data-bounds");

  if (notEmpty(bounds)) {
    this.cfg.bounds = bounds;
  }

  const fcPlaceholder = this.jq.closest(".ui-placeholder-component").length > 0;
  if (fcPlaceholder && this.cfg.toolbarVisible) {
    const container = byId(`${this.id}_toolbar`);
    byCssHtml(".ql-fc-placeholder", container)?.classList.add("fa", "fa-book");
    const modules = {
      toolbar: {
        container: container ?? null,
        handlers: {
          "fc-placeholder": bindThis((toolbar) => {
            const wrapper = toolbar.container?.closest(".row-input-wrapper") ?? undefined;
            const trigger = byCssAs(".ui-placeholder-trigger", HTMLButtonElement, wrapper);
            trigger?.click();
          }),
        },
      } satisfies import("quill/modules/toolbar.d.ts").ToolbarProps,
    };
    // Prevent PrimeFaces from changing the modules object to their own version without our handlers
    Object.defineProperty(this.cfg, "modules", {
      configurable: false,
      enumerable: true,
      writable: false,
      value: modules,
    });
  }

  superMethod();
}
